import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { AlertTriangle, Search, RefreshCw, Printer, Phone, MapPin, Users, Calendar } from "lucide-react";
import toast from "react-hot-toast";

const API = `${window.location.origin}/api`;

const formatIQD = (n) =>
  new Intl.NumberFormat("ar-IQ").format(Math.round(n || 0)) + " د.ع";

const formatDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("ar-IQ"); }
  catch { return iso; }
};

const getSeverity = (days) => {
  if (days >= 90) return { color: "bg-rose-600 text-white", label: "خطير" };
  if (days >= 30) return { color: "bg-rose-500 text-white", label: "متأخر جداً" };
  if (days >= 7) return { color: "bg-amber-500 text-white", label: "متأخر" };
  return { color: "bg-yellow-500 text-slate-900", label: "قريب" };
};

export default function OverdueReportPage() {
  const [data, setData] = useState({ overdue: [], stats: null });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/overdue/installments`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData({ overdue: res.data.overdue || [], stats: res.data.stats || null });
    } catch (e) {
      toast.error("فشل جلب تقرير المتأخرين");
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return data.overdue;
    return data.overdue.filter((x) => {
      const hay = [x.customer_name, x.customer_phone, x.guarantor_name, x.customer_address]
        .filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [data.overdue, search]);

  const handlePrint = () => window.print();

  return (
    <div className="space-y-6 print:bg-white print:text-black" data-testid="overdue-report-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 print:hidden">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <AlertTriangle className="w-6 h-6 text-rose-400" />
            تقرير المتأخرين
          </h1>
          <p className="text-slate-400 text-sm mt-1">كل الأقساط المتأخرة عن موعد الاستحقاق مع بيانات العميل والكفيل</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData} className="border-slate-600 text-slate-200">
            <RefreshCw className="w-4 h-4 me-2" />تحديث
          </Button>
          <Button onClick={handlePrint} className="bg-emerald-600 hover:bg-emerald-700" data-testid="print-overdue-btn">
            <Printer className="w-4 h-4 me-2" />طباعة
          </Button>
        </div>
      </div>

      {data.stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Card className="bg-slate-800/60 border-slate-700">
            <CardContent className="p-4 flex items-center gap-3">
              <AlertTriangle className="w-8 h-8 text-rose-400" />
              <div>
                <div className="text-xs text-slate-400">عدد الأقساط المتأخرة</div>
                <div className="text-2xl font-bold text-rose-400" data-testid="overdue-count">{data.stats.total_count}</div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/60 border-slate-700">
            <CardContent className="p-4 flex items-center gap-3">
              <Users className="w-8 h-8 text-cyan-400" />
              <div>
                <div className="text-xs text-slate-400">عدد العملاء</div>
                <div className="text-2xl font-bold text-white">{data.stats.unique_customers}</div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/60 border-slate-700">
            <CardContent className="p-4">
              <div className="text-xs text-slate-400">الإجمالي الكلي</div>
              <div className="text-lg font-bold text-white mt-1">{formatIQD(data.stats.total_amount)}</div>
              <div className="text-xs text-emerald-400 mt-1">مسدد: {formatIQD(data.stats.total_paid)}</div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/60 border-rose-500/30">
            <CardContent className="p-4">
              <div className="text-xs text-rose-300">الباقي (المتأخر)</div>
              <div className="text-xl font-bold text-rose-400 mt-1" data-testid="overdue-remaining">
                {formatIQD(data.stats.total_remaining)}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <Card className="bg-slate-800/60 border-slate-700 print:hidden">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="ابحث باسم العميل، الهاتف، الكفيل..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="bg-slate-700 border-slate-600 text-white pr-10"
              data-testid="overdue-search-input"
            />
          </div>
        </CardContent>
      </Card>

      <Card className="bg-slate-800/60 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white text-lg">القائمة التفصيلية ({filtered.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center text-slate-400 py-12">جاري التحميل...</div>
          ) : filtered.length === 0 ? (
            <div className="text-center text-emerald-400 py-12" data-testid="overdue-empty">
              <div className="text-4xl mb-2">🎉</div>
              لا توجد أقساط متأخرة — ممتاز!
            </div>
          ) : (
            <div className="space-y-2">
              {filtered.map((item) => {
                const sev = getSeverity(item.days_overdue);
                return (
                  <div
                    key={item.id}
                    className="border border-slate-700 rounded-lg p-4 hover:bg-slate-700/30 print:border-slate-300"
                    data-testid={`overdue-row-${item.id}`}
                  >
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
                      <div className="flex-1 space-y-1.5">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-white text-base">{item.customer_name || "—"}</span>
                          <Badge className={sev.color}>
                            {item.days_overdue} يوم — {sev.label}
                          </Badge>
                        </div>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-400">
                          {item.customer_phone && (
                            <a href={`tel:${item.customer_phone}`} className="flex items-center gap-1 hover:text-emerald-400">
                              <Phone className="w-3 h-3" /> {item.customer_phone}
                            </a>
                          )}
                          {item.customer_address && (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" /> {item.customer_address}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" /> استحقاق: {formatDate(item.due_date)}
                          </span>
                        </div>
                        {item.guarantor_name && (
                          <div className="text-xs text-amber-300 bg-amber-500/10 px-2 py-1 rounded inline-block">
                            🤝 الكفيل: {item.guarantor_name}
                            {item.guarantor_phone && ` • ${item.guarantor_phone}`}
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-0.5 shrink-0">
                        <div className="text-rose-400 font-bold text-lg">
                          {formatIQD(item.remaining)}
                        </div>
                        <div className="text-xs text-slate-500">
                          أصل: {formatIQD(item.amount)} • مسدد: {formatIQD(item.paid)}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
